import React, { useRef } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import ActionGroup from "./components/actionGroup";
import getStyled from "../../hooks/getStyled";
import TitleGroup from "./components/titleGroup";
import ActionConfirmation from "./components/actionConfirmation";
import useOverflowingActions from "./helpers/useOverflowingActions";
import {
  ACTION_NAMES,
  ACTION_TYPES,
  PAGE_TITLE_STATUS,
} from "../../constants/actionBar";
import CreateReactPortal from "../../hooks/createReactPortal";
import { processFilterActionDef } from "./helpers/processFilterActionDef";
import { processIconActionDef } from "./helpers/processIconActionDef";
import { hasPermission } from "../authorized";
import { processGroupByActionDef } from "./helpers/processRowGroupingActionDef";
import { processButtonActionDef } from "./helpers/processButtonActionDef";

const { styled } = getStyled();

// Used to style the action bar (height, padding)
const ActionBarContentWrapper = styled("div")(({ theme }) => ({
  height: "48px",
  paddingLeft: theme.spacing(2.5),
  paddingRight: theme.spacing(1.25),
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
}));

// Used to create a space between the title and the actions
const DivStyled = styled("div")(({ theme }) => ({
  height: "48px",
  minWidth: "20px",
  width: "50px",
  [theme.breakpoints.down("sm")]: {
    // Reduce space between title and actions < 600px
    width: "20px",
  },
}));

const SFActionBar = ({
  actionDefs = [],
  navDefs = [],
  titleProps = {},
  search = false,
  searchProps = {},
  disablePortal = false,
}) => {
  const ref = useRef(null);
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [overflowingActions] = useOverflowingActions(actionDefs, ref);

  const [confirmation, setConfirmation] = React.useState({
    open: false,
    header: "",
    message: "",
    primaryLabel: "",
    primaryDisabled: false,
    secondaryLabel: "",
    secondaryDisabled: false,
    variant: "info",
    onConfirm: () => {},
    async: false,
    onError: () => {},
  });

  // This function is used to get the names of all the actions recursively
  const getActionNames = (actionDefs) => {
    return actionDefs.map((def) => {
      if (def.children) {
        return [def.name, ...getActionNames(def.children)];
      }
      return def.name;
    });
  };

  const actionNames = getActionNames(actionDefs).flat();

  // This function is used to check if there are any duplicate action names
  const checkDuplicateNames = (actionDefs) => {
    actionDefs.forEach((def) => {
      if (actionNames.filter((name) => name === def.name).length > 1) {
        throw new Error(
          `SFActionBar: Action names must be unique. Duplicate name found: ${def.name}`
        );
      }
    });
  };

  checkDuplicateNames(actionDefs);

  // This function is used to filter out the action definitions that the user does not have permission to see recursively
  // and also filter out actions that have hide=true
  const removeActionsWithoutPermission = (actionDefs) => {
    return actionDefs.filter((def) => {
      // Skip actions that have hide=true
      if (def.hide === true) {
        return false;
      }

      if (!def.permissions || hasPermission(def.permissions)) {
        if (def.children) {
          const filteredChildren = removeActionsWithoutPermission(def.children);
          // Return the action with filtered children
          return {
            ...def,
            children: filteredChildren,
          };
        }
        return true;
      }
      return false;
    });
  };

  // This function is used to filter out the navDefs definitions that the user does not have permission to see recursively
  const removeNavDefsWithoutPermission = (navDefs) =>
    navDefs.filter((def) => !def.permissions || hasPermission(def.permissions));

  // This function is used to determine the click handler of each action depending on whether it requires confirmation
  const getProcessedClickHandler = (actionDef) => {
    if (!actionDef.confirmation) {
      // If the action does not require confirmation, return the original onClick
      return actionDef.onClick || (() => {});
    } else {
      // If the action requires confirmation, return a function that sets the confirmation state
      // (the onClick will be called when the user confirms the action from the modal)
      let async = false;
      let onError = (err) => {};

      switch (actionDef.type) {
      case ACTION_TYPES.ICON:
        async = actionDef.iconProps?.async;
        onError = actionDef.iconProps?.onError || ((err) => {});
        break;
      case ACTION_TYPES.BUTTON:
        async = actionDef.buttonProps?.async;
        onError = actionDef.buttonProps?.onError || ((err) => {});
        break;
      default:
        break;
      }

      return () =>
        setConfirmation({
          open: true,
          header: actionDef.confirmationProps?.header,
          message: actionDef.confirmationProps?.message,
          primaryLabel: actionDef.confirmationProps?.primaryLabel,
          primaryDisabled: actionDef.confirmationProps?.primaryDisabled,
          secondaryLabel: actionDef.confirmationProps?.secondaryLabel,
          secondaryDisabled: actionDef.confirmationProps?.secondaryDisabled,
          variant: actionDef.confirmationProps?.variant ?? "info",
          onConfirm: actionDef.onClick || (() => {}),
          async,
          onError,
        });
    }
  };

  // This function is used to process the click handlers of every action definition recursively
  const processClickHandlers = (actionDefs) => {
    return actionDefs.map((actionDef) => {
      if (actionDef.children) {
        return {
          ...actionDef,
          onClick: getProcessedClickHandler(actionDef),
          children: processClickHandlers(actionDef.children),
        };
      }
      return {
        ...actionDef,
        onClick: getProcessedClickHandler(actionDef),
      };
    });
  };

  const processedActionDefs = processClickHandlers(
    removeActionsWithoutPermission(actionDefs)
  )
    // The definitions of the icon and button actions should be processed before rendering
    .map((def) => {
      if (def.type === ACTION_TYPES.ICON) {
        return processIconActionDef(def, t);
      } else if (def.type === ACTION_TYPES.BUTTON) {
        return processButtonActionDef(def, t);
      } else {
        return def;
      }
    })
    // The definitions of the filter and group-by icon actions should be processed before rendering
    .map((def) => {
      if (def.type === ACTION_TYPES.ICON && def.name === ACTION_NAMES.FILTER) {
        return processFilterActionDef(def, t, dispatch);
      } else if (
        def.type === ACTION_TYPES.ICON &&
        def.name === ACTION_NAMES.GROUP_BY
      ) {
        return processGroupByActionDef(def, t, dispatch);
      } else {
        return def;
      }
    });

  return CreateReactPortal(
    <>
      <ActionBarContentWrapper ref={ref}>
        <TitleGroup
          title={titleProps.title ?? t("components.actionBar.blank")}
          code={titleProps.code}
          status={titleProps.status}
          tooltip={titleProps.tooltip}
          navDefs={removeNavDefsWithoutPermission(navDefs)}
          navMenuAnchorEl={ref.current}
        />
        <DivStyled />
        <ActionGroup
          // Only pass the actionDefs that are not overflowing into the action group
          actionDefs={processedActionDefs.filter(
            (actionDef) => !overflowingActions.includes(actionDef.name)
          )}
          // Pass the overflowDefs to the action group so that it can render the more action
          overflowDefs={processedActionDefs.filter((def) =>
            overflowingActions.includes(def.name)
          )}
          search={search}
          searchProps={searchProps}
        />
      </ActionBarContentWrapper>
      <ActionConfirmation
        open={confirmation.open}
        title={confirmation.header}
        message={confirmation.message}
        primaryLabel={confirmation.primaryLabel}
        primaryDisabled={confirmation.primaryDisabled}
        secondaryLabel={confirmation.secondaryLabel}
        secondaryDisabled={confirmation.secondaryDisabled}
        variant={confirmation.variant ?? "info"}
        onConfirm={confirmation.onConfirm}
        onClose={() => {
          setConfirmation((prev) => ({ ...prev, open: false }));
        }}
        async={confirmation.async}
        onError={confirmation.onError}
      />
    </>,
    document.getElementById("actionBarRoot"),
    disablePortal
  );
};

export default SFActionBar;

SFActionBar.propTypes = {
  /**
   * The definitions of the icons, switches, buttons and add-ons to display in the action bar. Each action definition should contain the following properties:
   * - `name*`: The name of the action (must be unique, or an error will be thrown).
   * - `type*`: The type of the action (`"icon"`, `"switch"`, `"button"` or `"addOn"`).
   * - `onClick`: Callback fired when the action is clicked.
   * - `iconProps`: The props to pass to the icon renderer, if the action `type` is `"icon"`:
   *  - `icon*`: The icon to display for the action.
   *  - `tooltip*`: The tooltip to display for the icon when hovered.
   *  - `overflowLabel*`: The label to display in the overflow dropdown menu if the action does not fit in the action bar (only icon actions are allowed to overflow).
   *  - `label`: Optional label to display to the right of the icon.
   *  - `async`: If `true`, the `onClick` needs to be an async function. Requires `confirmation` to be `true`. A loading spinner will be displayed on the confirmation modal's primary button while the async `onClick` is being processed.
   *  - `onError`: Callback fired when the async `onClick` has an error. Receives the error as an argument.
   *  - `disableTooltip`: If `true`, the tooltip will not be displayed.
   *  - `className`: The class name for the icon.
   *  - `disabledReason`: The reason why the icon is disabled. If provided and the icon is disabled, the reason will be displayed in a tooltip when hovered.
   * - `switchProps`: The props to pass to the switch renderer, if the action `type` is `"switch"`:
   *  - `onLabel*`: The label to display to the right of the switch when it is on.
   *  - `offLabel`: Optional label to display to the right of the switch when it is off (if not provided, the switch will display the `onLabel` when off as well).
   *  - `initial`: The initial state of the switch (`"on"` or `"off"`). Only used when not in controlled mode.
   *  - `controlled`: If `true`, the switch state is controlled from outside via the `value` prop.
   *  - `value`: The current value of the switch when in controlled mode (must be provided when `controlled` is `true`).
   * - `buttonProps`: The props to pass to the button renderer, if the action `type` is `"button"`:
   *  - `label*`: The label of the button.
   *  - `variant*`: The variant of the button (`"primary"`, `"secondary"` or `"utility"`).
   *  - `icon`: Optional icon to display inside the button.
   *  - `iconPlacement`: The placement of the icon inside the button (`"start"` or `"end"`).
   *  - `async`: If `true`, the `onClick` needs to be an async function. The button renderer will display a loading spinner while the async `onClick` is being processed. If combined with `confirmation`, the loading spinner will be displayed on the confirmation modal's primary button.
   *  - `onError`: Callback fired when the async `onClick` has an error. Receives the error as an argument.
   *  - `disabledReason`: The reason why the button is disabled. If provided and the button is disabled, the reason will be displayed in a tooltip when hovered.
   * - `confirmation`: If `true`, the action's `onClick` will only be called after the user confirms the action from a modal.
   * - `confirmationProps`: The props to pass to the confirmation modal of the action:
   *  - `message*`: The message of the confirmation modal.
   *  - `header`: Custom header for the confirmation modal.
   *  - `primaryLabel`: Custom label for the primary button of the confirmation modal.
   *  - `primaryDisabled`: If `true`, the primary button of the confirmation modal will be disabled.
   *  - `secondaryLabel`: Custom label for the secondary button of the confirmation modal.
   *  - `secondaryDisabled`: If `true`, the secondary button of the confirmation modal will be disabled.
   *  - `variant`: The variant of the confirmation modal (`"info"`, `"error"` or `"warning"`).
   * - `disabled`: If `true`, the action will be disabled.
   * - `selectedChild`: The name of the child action to be selected by default when toggling the parent action.
   * - `permissions`: An array of permission strings that the user must have in order to see the action.
   * - `clearSelection`: Useful for clearing the selection of the active action.
   * - `children`: An array of child action definitions. If provided, the parent action will open a menu with the child actions as items when clicked.
   * - `component`: The component to render for the action if the action `type` is `"custom"`.
   * - `hide`: If `true`, the action will not be displayed.
   * Child action definitions should contain the following properties:
   *  - `name*`: The name of the action (must be unique, otherwise an error will be thrown).
   *  - `label*`: The label of the action in the menu.
   *  - `onClick*`: Callback fired when the action is clicked.
   *  - `confirmation`: See above for details.
   *  - `confirmationProps`: See above for details.
   *  - `disabled`: See above for details.
   *  - `selectedChild`: See above for details.
   *  - `permissions`: See above for details.
   *  - `children` can be nested to create multiple levels of actions.
   */
  actionDefs: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      type: PropTypes.oneOf(Object.values(ACTION_TYPES)).isRequired,
      onClick: PropTypes.func,
      iconProps: PropTypes.shape({
        icon: PropTypes.element,
        tooltip: PropTypes.string,
        overflowLabel: PropTypes.string,
        label: PropTypes.string,
        async: PropTypes.bool,
        onError: PropTypes.func,
        disableTooltip: PropTypes.bool,
        className: PropTypes.string,
      }),
      switchProps: PropTypes.shape({
        onLabel: PropTypes.string.isRequired,
        offLabel: PropTypes.string,
        initial: PropTypes.oneOf(["on", "off"]),
        controlled: PropTypes.bool,
        value: PropTypes.bool,
      }),
      buttonProps: PropTypes.shape({
        label: PropTypes.string.isRequired,
        variant: PropTypes.oneOf(["primary", "secondary", "utility"]),
        icon: PropTypes.element,
        iconPlacement: PropTypes.oneOf(["start", "end"]),
        async: PropTypes.bool,
        onError: PropTypes.func,
        disabledReason: PropTypes.string,
      }),
      confirmation: PropTypes.bool,
      confirmationProps: PropTypes.shape({
        message: PropTypes.string.isRequired,
        header: PropTypes.string,
        primaryLabel: PropTypes.string,
        primaryDisabled: PropTypes.bool,
        secondaryLabel: PropTypes.string,
        secondaryDisabled: PropTypes.bool,
        variant: PropTypes.oneOf(["info", "error", "warning"]),
      }),
      children: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string.isRequired,
          label: PropTypes.string.isRequired,
          onClick: PropTypes.func.isRequired,
        })
      ),
      component: PropTypes.element,
      disabled: PropTypes.bool,
      selectedChild: PropTypes.string,
      permissions: PropTypes.arrayOf(PropTypes.string),
      clearSelection: PropTypes.bool,
      hide: PropTypes.bool,
    })
  ),
  /**
   * Props required for the proper functioning of the filter icon action (if provided in the `actionDefs`):
   * - `gridName*`: The name of the grid to apply the filters to. Must be provided in the `filterParams` of the filter action definition, otherwise an error will be thrown.
   * If provided, the basic filters option will be displayed in the filter action menu, which will open the basic filters panel when clicked.
   * - `advancedQuery`: If `true`, the advanced query and saved queries options will be displayed in the filter action menu.
   */
  filterParams: PropTypes.shape({
    gridName: PropTypes.string.isRequired,
    advancedQuery: PropTypes.bool,
  }),
  /**
   * Props required for the proper functioning of the group-by icon action (if provided in the `actionDefs`):
   * - `gridName*`: The name of the grid to apply the row grouping to. Must be provided in the `groupParams` of the group-by action definition, otherwise an error will be thrown.
   * - `gridLayout`: The layout of the grid, stored in the redux store. Must be provided in the `groupParams` of the group-by action definition, otherwise an error will be thrown.
   */
  groupParams: PropTypes.shape({
    gridName: PropTypes.string.isRequired,
    gridLayout: PropTypes.object,
  }),
  /**
   * The definitions of the third level nav items. If at least one nav item definition is provided, a nav button will be displayed in the action bar
   * that will open a menu with the corresponding nav items when clicked. Each nav item definition should contain the following properties:
   * - `path*`: The path to navigate to when the nav item is clicked.
   * - `icon*`: The icon to display for the nav item.
   * - `header*`: The header of the nav item.
   * - `description*`: The description of the nav item.
   * - `default`: If `true`, the nav item will have a checked PC icon in the top right corner.
   * - `highlight`: An element to display below the description of the nav item.
   */
  navDefs: PropTypes.arrayOf(
    PropTypes.shape({
      path: PropTypes.string.isRequired,
      icon: PropTypes.element.isRequired,
      header: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      default: PropTypes.bool,
      highlight: PropTypes.element,
    })
  ),
  /** If `true`, a search input component will be displayed in the action bar. */
  search: PropTypes.bool,
  /** The props to pass to the search input component:
   * - `dropdownOptions`: An array of strings to display as selectable options in the search input dropdown.
   * - `initialInput`: The initial value to display in the search input.
   * - `initialSelection`: An array of strings to pre-select in the search input dropdown.
   * - `onInputChange`: The function to call when the input value changes. It receives the current input value as an argument.
   * - `onSelectionChange`: The function to call when a dropdown option is selected or deselected. It receives the array of currently selected options as an argument.
   * - `gridName`: The name of the grid to which the search action is associated. Used for setting the initial input value of the search field based on the grid's search text filter value (`initialInput` is ignored if `gridName` is provided).
   */
  searchProps: PropTypes.shape({
    dropdownOptions: PropTypes.arrayOf(PropTypes.string),
    initialInput: PropTypes.string,
    initialSelection: PropTypes.arrayOf(PropTypes.string),
    onInputChange: PropTypes.func,
    onSelectionChange: PropTypes.func,
    gridName: PropTypes.string,
  }),
  /** The props to pass to the title component of the action bar:
   * - `title*`: The page title to display in the action bar.
   * - `code`: Short text to display next to the title in parentheses.
   * - `tooltip`: The text to display in a tooltip when hovering over an info icon that will be displayed next to the title.
   * - `status`: A status indicator to display next to the title (`"active"` or `"manuallyUpdated"`).
   */
  titleProps: PropTypes.shape({
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
    code: PropTypes.string,
    tooltip: PropTypes.string,
    status: PropTypes.oneOf(Object.values(PAGE_TITLE_STATUS)),
  }),
  /**
   * If `true`, the action bar will not be rendered in a portal.
   */
  disablePortal: PropTypes.bool,
};
